import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Star, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { fetchCMSContent } from '../lib/cms';

export default function Home() {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [visibleCount, setVisibleCount] = useState(8);

  useEffect(() => {
    const loadData = async () => {
      const servicesData = await fetchCMSContent('services');
      const galleryData = await fetchCMSContent('gallery');
      setServices(servicesData);
      setGallery(galleryData);
    };
    loadData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Professionel Bilpleje &amp; Coating i Billund | MJ Detailing</title>
        <meta name="description" content="Få professionel bilpleje, polering &amp; keramisk coating i Billund hos MJ Detailing. Premium kvalitet &amp; 100% tilfredshedsgaranti. Få et gratis tilbud nu!" />
      </Helmet>

      <Hero />

      {/* Intro Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-zinc-500 font-bold tracking-widest uppercase text-sm mb-4 block">Hvem er vi?</span>
              <h2 className="text-4xl md:text-5xl font-black tracking-normal mb-8 leading-tight">
                DIN BIL FORTJENER <br />
                <span className="bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">DET BEDSTE</span>
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Hos MJ Detailing er vi passionerede omkring biler. Vi ved, at din bil er mere end bare et transportmiddel – det er en investering og en kilde til glæde. Derfor går vi aldrig på kompromis med kvaliteten.
              </p>
              <div className="space-y-4 mb-10">
                {[
                  'Certificerede produkter',
                  'Mange års erfaring',
                  'Øje for detaljen',
                  '100% tilfredshedsgaranti'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="text-white" size={20} />
                    <span className="font-medium text-zinc-200">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/om-os" className="inline-flex items-center gap-2 text-white font-bold border-b-2 border-white pb-1 hover:text-zinc-400 hover:border-zinc-400 transition-all">
                Læs mere om os <ArrowRight size={18} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative aspect-square md:aspect-[16/10] lg:aspect-square rounded-2xl overflow-hidden border border-white/10"
            >
              <img
                src="https://assets.auxodev.dk/assets/f1317603-54a7-41be-bffe-0256114ce69f/7b9c8ec3-f7a0-47a1-8e8d-afe45910d4e3.jpg"
                alt="MJ Detailing Work"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-zinc-500 font-bold tracking-widest uppercase text-sm mb-4 block">Vores Ydelser</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-normal">HVAD KAN VI GØRE FOR DIG?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group relative bg-black border border-white/5 rounded-3xl overflow-hidden hover:border-white/20 transition-all ${
                  index === 2 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image?.[0]}
                    alt={service.name?.da}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-black tracking-normal mb-4 uppercase">{service.name?.da}</h3>
                  <div
                    className="text-zinc-400 text-sm mb-6 line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: service.description?.da }}
                  />
                  <Link
                    to={`/ydelser/${service.slug?.da || service.id}`}
                    className="flex items-center gap-2 text-white font-bold text-sm uppercase tracking-widest group-hover:gap-4 transition-all"
                  >
                    Læs Mere <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="pt-16 md:pt-20 lg:pt-32 pb-16 md:pb-32 bg-black relative overflow-hidden">
        {/* Background Accents */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-zinc-900/20 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-zinc-900/20 rounded-full blur-3xl translate-y-1/2" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mb-20">
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-zinc-500 font-bold tracking-[0.2em] uppercase text-xs mb-4 block"
            >
              Hvorfor vælge MJ Detailing?
            </motion.span>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-5xl font-black tracking-normal leading-none"
            >
              VI GÅR OP I <br />
              <span className="bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">DETALJEN</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: <Star className="w-6 h-6" />, 
                title: 'Premium Kvalitet', 
                desc: 'Vi anvender udelukkende de mest eksklusive produkter fra verdens førende brands.' 
              },
              { 
                icon: <Shield className="w-6 h-6" />, 
                title: 'Lang Beskyttelse', 
                desc: 'Vores keramiske coatings sikrer din bils lak mod elementerne i flere år.' 
              },
              { 
                icon: <Zap className="w-6 h-6" />, 
                title: 'Hurtig Service', 
                desc: 'Effektivitet uden kompromis. Vi leverer resultater i topklasse til aftalt tid.' 
              },
              { 
                icon: <CheckCircle2 className="w-6 h-6" />, 
                title: 'Ekspertise', 
                desc: 'Med mange års erfaring håndterer vi alt fra klassiske biler til moderne superbiler.' 
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group p-8 rounded-3xl bg-zinc-950 border border-white/5 hover:border-white/20 transition-all duration-500 hover:-translate-y-2"
              >
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 flex items-center justify-center text-white mb-8 group-hover:bg-white group-hover:text-black transition-colors duration-500 shadow-xl shadow-black">
                  {item.icon}
                </div>
                <h4 className="text-xl font-bold tracking-normal uppercase mb-4">{item.title}</h4>
                <p className="text-zinc-500 text-sm leading-relaxed group-hover:text-zinc-400 transition-colors">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div>
              <span className="text-zinc-500 font-bold tracking-widest uppercase text-sm mb-4 block">Galleri</span>
              <h2 className="text-3xl md:text-5xl font-black tracking-normal">UDVALGTE RESULTATER</h2>
            </div>
            <Link to="/kontakt" className="bg-white text-black px-6 md:px-12 py-3 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-zinc-200 transition-all">
              Book din tid nu
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.slice(0, visibleCount).map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="aspect-square rounded-xl overflow-hidden border border-white/5 group relative cursor-pointer"
              >
                <img
                  src={item.image?.[0]}
                  alt={item.title?.da}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-4 text-center">
                  <span className="text-xs font-bold uppercase tracking-widest">{item.category}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {visibleCount < gallery.length && (
            <div className="mt-12 text-center">
              <button
                onClick={() => setVisibleCount(prev => prev + 8)}
                className="inline-flex items-center gap-2 text-white font-bold border-b-2 border-white pb-1 hover:text-zinc-400 hover:border-zinc-400 transition-all uppercase text-sm tracking-widest"
              >
                Se mere
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-32 bg-black relative overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-zinc-900/30 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto bg-zinc-950 border border-white/5 rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden">
            {/* Subtle inner glow */}
            <div className="absolute inset-0 bg-linear-to-b from-white/[0.02] to-transparent pointer-events-none" />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-1.5 mb-8 text-xs font-bold tracking-[0.3em] uppercase bg-white/5 border border-white/10 rounded-full text-zinc-400">
                Tag det næste skridt
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-normal mb-10 leading-[0.9]">
                KLAR TIL AT GIVE <br className="hidden md:block" /> DIN BIL <span className="bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">NYT LIV?</span>
              </h2>
              <p className="text-xl text-zinc-400 mb-14 max-w-2xl mx-auto leading-relaxed">
                Kontakt os i dag for et uforpligtende tilbud på din bilpleje. Vi glæder os til at høre fra dig og transformere din bil.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Link
                  to="/kontakt"
                  className="group flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-lg font-bold hover:bg-zinc-200 transition-all w-full sm:w-auto justify-center shadow-2xl shadow-white/10"
                >
                  Få Et Tilbud Nu
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="tel:+4527895995"
                  className="flex items-center justify-center gap-3 text-white px-8 py-4 rounded-full text-lg font-bold border border-white/10 bg-white/5 hover:bg-white/10 backdrop-blur-sm transition-all w-full sm:w-auto"
                >
                  Ring +45 27 89 59 95
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
